import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { MODALITIES, relativeMinutes, type Modality } from "@/lib/modality";

export const Route = createFileRoute("/admin/rides")({
  component: AdminRides,
});

interface Row {
  id: string;
  modality: Modality;
  pace: string;
  start_time: string;
  start_address: string | null;
  status: string;
  creator_id: string;
  creator_nickname?: string;
  riders?: number;
}

function AdminRides() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data: rides } = await supabase
      .from("rides")
      .select("id, modality, pace, start_time, start_address, status, creator_id")
      .eq("status", "active")
      .order("start_time", { ascending: true });
    const list = (rides as Row[]) || [];

    if (list.length) {
      const ids = list.map((r) => r.creator_id);
      const [{ data: profs }, { data: parts }] = await Promise.all([
        supabase.from("profiles").select("id, nickname").in("id", ids),
        supabase.from("ride_participants").select("ride_id").in("ride_id", list.map((r) => r.id)),
      ]);
      const nicks = new Map((profs || []).map((p: any) => [p.id, p.nickname]));
      const counts: Record<string, number> = {};
      (parts || []).forEach((p: any) => { counts[p.ride_id] = (counts[p.ride_id] || 0) + 1; });
      list.forEach((r) => {
        r.creator_nickname = nicks.get(r.creator_id) || "—";
        r.riders = counts[r.id] || 0;
      });
    }
    setRows(list);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const cancel = async (id: string) => {
    const reason = window.prompt("Motivo de cancelación:");
    if (reason === null) return;
    const { error } = await supabase
      .from("rides")
      .update({ status: "cancelled", cancelled_reason: reason || "Cancelada por admin" })
      .eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Salida cancelada"); load(); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl">Salidas activas</h1>
      <p className="text-sm text-muted-foreground">Cancela cualquier salida con motivo.</p>

      <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-surface">
        <div className="grid grid-cols-[1fr_0.7fr_0.6fr_1fr_0.8fr_0.5fr_0.7fr] gap-3 border-b border-border bg-[#0f0f0f] px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">
          <div>Creador</div><div>Modalidad</div><div>Ritmo</div><div>Lugar</div><div>Cuándo</div><div>Riders</div><div className="text-right">Acciones</div>
        </div>
        {loading ? <div className="p-6 text-sm text-muted-foreground">Cargando…</div>
        : rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">Sin salidas activas.</div>
        : rows.map((r) => {
          const m = MODALITIES.find((mm) => mm.value === r.modality);
          return (
            <div key={r.id} className="grid grid-cols-[1fr_0.7fr_0.6fr_1fr_0.8fr_0.5fr_0.7fr] gap-3 items-center border-b border-border px-4 py-3 text-sm last:border-0">
              <div className="truncate font-medium">@{r.creator_nickname}</div>
              <div className="text-xs">{m?.label}</div>
              <div className="text-xs capitalize">{r.pace}</div>
              <div className="truncate text-xs text-muted-foreground">{r.start_address?.split(",")[0] ?? "—"}</div>
              <div className="text-xs">{relativeMinutes(r.start_time)}</div>
              <div className="text-xs">{r.riders}</div>
              <div className="flex justify-end">
                <button
                  onClick={() => cancel(r.id)}
                  className="rounded-full border border-red-500/40 px-3 py-1 text-xs text-red-400 hover:bg-red-500/5"
                >
                  Cancelar
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
